// 공종 시공반장 상태 — 공통코드 E01_TASK_PARTNER_STATUS (작업계획서 §2.3·§2.4).
// 상태 코드 문자열·판정은 이 파일에서만 정의하고 화면은 아래 상수·헬퍼를 재사용한다.

import { formatMonthDay, formatMonthDayTime } from '../components/PartnerCandidateItem'

export const PARTNER_STATUS = {
  UNASSIGNED:   'UNASSIGNED',   // 반장 미지정
  UNASSIGNABLE: 'UNASSIGNABLE', // 자동배정 시도 결과 후보 없음
  ASSIGNED:     'ASSIGNED',     // 배정됨, 일정확인 미발송
  WAITING:      'WAITING',      // 발송함, 회신 대기
  CONFIRMED:    'CONFIRMED',    // 가능 회신으로 일정 확정
  DECLINED:     'DECLINED',     // 불가 회신
  UNFIXED:      'UNFIXED',      // 한도 소진·자동 발송 꺼짐 상태로 기한 지남
} as const

export type PartnerStatus = typeof PARTNER_STATUS[keyof typeof PARTNER_STATUS]

/** 일정확인 발송 방식(서버 ForemanConfirmSendMethod). */
export type ConfirmSendMethod = 'MANUAL' | 'AUTO' | 'RESEND' | 'CHANGE'

/** 공종의 마지막 전달된 일정확인 요청 요약(서버 공종 응답·단건 API `confirmRequest`). */
export interface ConfirmRequest {
  historyId: number
  attemptNo: number | null
  sendMethod: ConfirmSendMethod | string
  partnerId: number | null
  /** 요청(발송) 일시(ISO) */
  sentDtm: string
  replyExpireDtm: string | null
  responseResult: 'CONFIRMED' | 'DECLINED' | null
  respondedDtm: string | null
  closedReason: 'EXPIRED' | 'REPLACED' | 'CHANGED' | null
  /** 이 공종에서 일정확인을 받은 반장의 순위(첫 전달 순서, 1부터) */
  rank: number
  /** rank≥2일 때 앞 순위 반장의 결과(DECLINED·EXPIRED·REPLACED·CHANGED·CONFIRMED), 없으면 null */
  previousOutcome: string | null
}

/** BE 공종 응답의 반장 상태 필드(공종 응답·캘린더·공유이력 공통 형태). */
export interface PartnerStatusFields {
  partnerStatus?: string | null
  /** 일정확인 발송 일시(ISO). null=미발송 */
  sentDtm?: string | null
  /** 반장 회신 일시(ISO). null=미회신 */
  respondedDtm?: string | null
  /** 마지막 전달된 일정확인 요청 요약. 없으면 null */
  confirmRequest?: ConfirmRequest | null
}

/** 반장 일정확인 회신 결과 표시 상태. */
export type ResponseState = 'ok' | 'no' | 'wait' | 'unfixed' | 'idle'

/** 반장 상태 → 회신 결과 표시 상태. WAITING=wait · CONFIRMED=ok · DECLINED=no · UNFIXED=unfixed · 그 외(미배정·배정불가·배정됨)=idle. */
export function getResponseState(task: { partnerStatus?: string | null }): ResponseState {
  switch (task.partnerStatus) {
    case PARTNER_STATUS.WAITING:   return 'wait'
    case PARTNER_STATUS.CONFIRMED: return 'ok'
    case PARTNER_STATUS.DECLINED:  return 'no'
    case PARTNER_STATUS.UNFIXED:   return 'unfixed'
    default:                       return 'idle'
  }
}

/** 반장이 일정 가능으로 회신해 확정된 공종인지. */
export function isConfirmed(task: { partnerStatus?: string | null }): boolean {
  return task.partnerStatus === PARTNER_STATUS.CONFIRMED
}

/** 일정확인을 발송한 적이 있는 공종인지(발송 일시 기준). */
export function isSent(task: { sentDtm?: string | null }): boolean {
  return !!task.sentDtm
}

/** 회신 결과 배지 — 문구는 기존(일정가능/일정불가/미응답) 유지, 미확정 추가, 그 외 상태는 '—'(마감 사유 표시는 4단계). */
export const RESPONSE_BADGE: Record<ResponseState, { status: 'success' | 'danger' | 'warning' | 'mute'; label: string }> = {
  ok:      { status: 'success', label: '✓ 일정가능' },
  no:      { status: 'danger',  label: '✕ 일정불가' },
  wait:    { status: 'warning', label: '⏳ 미응답' },
  unfixed: { status: 'danger',  label: '미확정' },
  idle:    { status: 'mute',    label: '—' },
}

/** 낙관적 갱신으로 화면 상태에 병합하는 값(전 필드 확정형). */
export interface PartnerStatusPatch {
  partnerStatus: PartnerStatus
  sentDtm: string | null
  respondedDtm: string | null
  /** 낙관적 갱신 시 서버 값을 모르는 필드는 null(이후 단건 API로 채운다) */
  confirmRequest?: ConfirmRequest | null
}

/** 반장 지정 변경 시 낙관적 갱신값 — 새 반장 있으면 ASSIGNED, 없으면 UNASSIGNED, 발송·회신 시각 초기화. */
export function partnerStatusAfterAssign(hasPartner: boolean): PartnerStatusPatch {
  return {
    partnerStatus: hasPartner ? PARTNER_STATUS.ASSIGNED : PARTNER_STATUS.UNASSIGNED,
    sentDtm: null,
    respondedDtm: null,
    confirmRequest: null,
  }
}

/** 착공 D-2 이내 수동 배정 즉시 확정 직후 화면 반영값 — CONFIRMED, 발송시각 없음, 회신시각=지금. */
export function partnerStatusAfterFixed(): PartnerStatusPatch {
  return { partnerStatus: PARTNER_STATUS.CONFIRMED, sentDtm: null, respondedDtm: new Date().toISOString(), confirmRequest: null }
}

/** 발송 응답에서 얻는 confirmRequest 일부 — rank·previousOutcome 은 단건 API 호출 결과로 덮어쓴다. */
export interface SentConfirmRequestInput {
  historyId?: number | null
  attemptNo?: number | null
  sendMethod: ConfirmSendMethod | string
  partnerId?: number | null
  replyExpireDtm: string | null
}

/**
 * 일정확인 발송 직후 화면 반영값 — 서버 응답(partnerStatus·sentDtm) 기준, 회신 시각 초기화. partnerStatus 생략 시 WAITING.
 * `sent` 를 주면 그 값으로 confirmRequest 임시값(rank 1)을 구성하고, 없으면 null(옛 요청 요약이 남지 않도록).
 * 호출부는 성공 직후 단건 API(getForemanConfirmRequest)로 rank·previousOutcome 까지 갱신한다.
 */
export function partnerStatusAfterSend(sentDtm: string, partnerStatus?: string | null, sent?: SentConfirmRequestInput | null): PartnerStatusPatch {
  return {
    partnerStatus: (partnerStatus as PartnerStatus | null | undefined) ?? PARTNER_STATUS.WAITING,
    sentDtm,
    respondedDtm: null,
    confirmRequest: sent ? {
      historyId: sent.historyId ?? 0,
      attemptNo: sent.attemptNo ?? null,
      sendMethod: sent.sendMethod,
      partnerId: sent.partnerId ?? null,
      sentDtm,
      replyExpireDtm: sent.replyExpireDtm,
      responseResult: null,
      respondedDtm: null,
      closedReason: null,
      rank: 1,
      previousOutcome: null,
    } : null,
  }
}

// ── 일정확인 발송 버튼 판정 (서버 ForemanConfirmSendService.checkAllowed 와 같은 기준) ──
// 서버 정본: MANUAL = 현재 반장 ASSIGNED·DECLINED·UNFIXED / 다른 반장 지정 UNASSIGNED·UNASSIGNABLE·ASSIGNED·WAITING·DECLINED·UNFIXED
//            RESEND = WAITING·CONFIRMED·DECLINED·UNFIXED (+ 전달된 이전 발송 있음, 공종·반장별 1번 — 서버가 판정)
//            확정(CONFIRMED) 반장을 다른 반장으로 바꾸는 것은 발송 API가 아니라 partner-changes(별도 단계).

export type SendMethod = 'MANUAL' | 'RESEND'

export interface SendAction {
  label: string
  enabled: boolean
  /** enabled=false 이면 null */
  method: SendMethod | null
  /** MANUAL로 다른 반장을 지정해 보내는 경우 true — 호출부가 partnerId를 함께 보낸다. */
  withPartnerId: boolean
}

/**
 * 공종 상태 + 선택한 반장 → 발송 버튼 라벨·활성 여부·방식(순수 함수).
 * @param task 공종의 현재 반장 상태(partnerStatus)
 * @param selected hasPartner=반장을 골랐는지 · isCurrent=고른 반장이 공종의 현재 반장 그대로인지
 */
export function getSendAction(
  task: { partnerStatus?: string | null },
  selected: { hasPartner: boolean; isCurrent: boolean },
): SendAction {
  const off = (label: string): SendAction => ({ label, enabled: false, method: null, withPartnerId: false })
  if (!selected.hasPartner) return off('반장님을 먼저 골라 주세요')
  const status = task.partnerStatus ?? PARTNER_STATUS.UNASSIGNED

  if (selected.isCurrent) {
    switch (status) {
      case PARTNER_STATUS.CONFIRMED:
        return off('일정 확정됨')
      case PARTNER_STATUS.WAITING:
      case PARTNER_STATUS.DECLINED:
      case PARTNER_STATUS.UNFIXED:
        // 이미 보낸 요청이 있는 반장에게 다시 보냄. 서버가 반장별 1번·전달 이력 유무를 판정(RESEND_LIMIT_EXCEEDED)
        return { label: '일정확인 카톡 다시 보내기', enabled: true, method: 'RESEND', withPartnerId: false }
      case PARTNER_STATUS.ASSIGNED:
        return { label: '일정확인 카톡 보내기', enabled: true, method: 'MANUAL', withPartnerId: false }
      default:
        // UNASSIGNED·UNASSIGNABLE 인데 반장이 있는 경우(화면 갱신 지연) — 서버가 지정 발송으로 처리하도록 partnerId 동봉
        return { label: '일정확인 카톡 보내기', enabled: true, method: 'MANUAL', withPartnerId: true }
    }
  }

  // 다른 반장을 골라 보냄 — 확정 반장 변경은 이 API가 아니라 partner-changes(별도 단계)
  if (status === PARTNER_STATUS.CONFIRMED) return off('확정된 반장은 반장 변경으로 바꿔 주세요')
  return { label: '일정확인 카톡 보내기', enabled: true, method: 'MANUAL', withPartnerId: true }
}

// ── 상세창 시공파트너 카드 상태 줄 (기획서 pstShow/pstTxt) ──
// 점 색: 미배정=빈 원(회색) · 노랑=배정·회신 대기 · 녹색=확정 · 빨강=배정불가·불가 회신·미확정

export type PartnerDotTone = 'none' | 'warning' | 'success' | 'danger'

export const PARTNER_STATUS_VIEW: Record<PartnerStatus, { label: string; tone: PartnerDotTone }> = {
  UNASSIGNED:   { label: '미배정',    tone: 'none' },
  ASSIGNED:     { label: '배정됨',    tone: 'warning' },
  WAITING:      { label: '회신 대기', tone: 'warning' },
  CONFIRMED:    { label: '일정가능', tone: 'success' },
  UNASSIGNABLE: { label: '배정불가',  tone: 'danger' },
  DECLINED:     { label: '불가 회신', tone: 'danger' },
  UNFIXED:      { label: '미확정',    tone: 'danger' },
}

/** 상태 코드 → 점·상태명. 알 수 없는 값·빈 값은 미배정으로 본다. */
export function getPartnerStatusView(status?: string | null): { label: string; tone: PartnerDotTone } {
  return PARTNER_STATUS_VIEW[(status ?? '') as PartnerStatus] ?? PARTNER_STATUS_VIEW.UNASSIGNED
}

/** 자동배정(AUTO)·직접지정(MANUAL) 출처 코드 — 서버 AssignSourceCode. */
export const ASSIGN_SOURCE = { AUTO: 'AUTO', MANUAL: 'MANUAL' } as const

// ── 반장 지정 변경 저장 판정 (서버 ForemanChangeService · ForemanReleaseService 와 같은 기준) ──
// 해제(DELETE partners/{id}) 허용 상태 = ASSIGNED·WAITING·DECLINED·UNFIXED, 확정 반장은 거부(422).
// 확정 반장을 다른 반장으로 바꾸는 것은 PATCH /tasks/{id} 가 아니라 partner-changes(사유 필수)만 쓴다.

export type PartnerSaveKind =
  | 'none'            // 바뀐 것 없음 · 호출 없음
  | 'patch'           // 기존 PATCH /tasks/{id} (assigneePartnerId)
  | 'change'          // 확정 반장 변경 — POST partner-changes
  | 'release'         // 지정 해제 — DELETE partners/{id}
  | 'release-blocked' // 확정 반장 해제 시도 — 호출 없이 안내

/**
 * @param selectedDirect 고른 반장이 결과만 직접등록(SIF-3)이면 true — 고르는 즉시 그 반장으로 확정하므로(사유·발송 없음)
 *                       확정 공종이어도 'change' 가 아니라 'patch' 로 저장한다(서버가 연락 방식으로 바로 확정)
 */
export function getPartnerSaveKind(
  task: { partnerStatus?: string | null; assigneePartnerId?: number | null },
  selectedPartnerId: number | null,
  selectedDirect = false,
): PartnerSaveKind {
  const current = task.assigneePartnerId ?? null
  if (selectedPartnerId === current) return 'none'
  const status = task.partnerStatus ?? PARTNER_STATUS.UNASSIGNED
  if (selectedPartnerId == null) {
    if (status === PARTNER_STATUS.CONFIRMED) return 'release-blocked'
    if (status === PARTNER_STATUS.ASSIGNED || status === PARTNER_STATUS.WAITING
      || status === PARTNER_STATUS.DECLINED || status === PARTNER_STATUS.UNFIXED) return 'release'
    return 'none'   // UNASSIGNED·UNASSIGNABLE — 서버에 해제할 반장이 없음
  }
  if (selectedDirect) return 'patch'
  if (status === PARTNER_STATUS.CONFIRMED && current != null) return 'change'
  return 'patch'
}

/** 확정 반장 변경 응답 → 화면 반영값. sendNow=true 면 WAITING+발송시각, false 면 ASSIGNED(서버 partnerStatus 그대로). */
export function partnerStatusAfterChange(r: {
  partnerStatus: string
  partnerId?: number
  sent: { historyId?: number; attemptNo?: number | null; sentDtm: string; replyExpireDtm: string | null } | null
  fixed?: boolean
}): PartnerStatusPatch {
  if (r.fixed) return partnerStatusAfterFixed()   // 착공 D-2 이내 즉시 확정
  if (r.sent) {
    return partnerStatusAfterSend(r.sent.sentDtm, r.partnerStatus, {
      historyId: r.sent.historyId, attemptNo: r.sent.attemptNo, sendMethod: 'CHANGE', partnerId: r.partnerId, replyExpireDtm: r.sent.replyExpireDtm,
    })
  }
  return { partnerStatus: r.partnerStatus as PartnerStatus, sentDtm: null, respondedDtm: null, confirmRequest: null }
}

/** 시공반장 팝업(SigongPopup) onAssigned 수신값 중 상태 병합에 쓰는 부분. */
export interface ForemanAssignedInput {
  foremanName: string | null
  /** 배정 반장이 실제로 바뀐 경우 true — 이전 응답(확인 상태) 초기화 */
  resetConfirm?: boolean
  sentDtm?: string
  partnerStatus?: string
  assigneePartnerId?: number | null
  assignSource?: string | null
  /** 착공 D-2 이내 즉시 확정 */
  fixed?: boolean
}

/**
 * 시공반장 팝업 배정·발송 결과를 공종 한 건에 국소 병합한다(상태·발송시각·반장 id·출처).
 * 반장 이름·연락처는 화면마다 필드명이 달라 호출부가 덮어쓴다.
 * 순서: 즉시 확정 → 반장 변경 초기화 → 발송 직후 상태. id·출처는 undefined 가 아닐 때만 병합.
 */
export function mergeForemanAssigned<T extends object>(task: T, u: ForemanAssignedInput): T & Partial<PartnerStatusPatch> {
  return {
    ...task,
    ...(u.fixed ? partnerStatusAfterFixed() : u.resetConfirm ? partnerStatusAfterAssign(!!u.foremanName) : {}),
    ...(u.sentDtm ? partnerStatusAfterSend(u.sentDtm, u.partnerStatus) : {}),
    ...(u.assigneePartnerId !== undefined ? { assigneePartnerId: u.assigneePartnerId } : {}),
    ...(u.assignSource !== undefined ? { assignSource: u.assignSource } : {}),
  }
}

// ── 상태 줄 문구 · 진행 요약 단계 (순수 함수) ──

/** 방금 발송으로 보는 경과 한계(ms) — 직접 발송(MANUAL·RESEND·CHANGE) 후 5분 */
export const JUST_SENT_WINDOW_MS = 5 * 60 * 1000

/** 서버 일시 문자열(KST 로컬)을 브라우저 로컬 시각으로 파싱 — formatMonthDayTime 과 같은 정규식 방식. 실패 시 null. */
export function parseLocalDtmMs(s: string | null | undefined): number | null {
  if (!s) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0)).getTime()
}

/** 상태 줄 입력(공종의 반장 상태·출처·발송/회신 시각·마지막 요청 요약). */
export interface PartnerStatusLineTask {
  partnerStatus?: string | null
  assignSource?: string | null
  sentDtm?: string | null
  respondedDtm?: string | null
  confirmRequest?: ConfirmRequest | null
  /** 배정 시점 연락 방식(SIF-3) — 'N'=결과만 직접등록(일정확인 회신 없음) */
  commYn?: string | null
}

const DIRECT_SEND_METHODS: readonly string[] = ['MANUAL', 'RESEND', 'CHANGE']
const FIRST_SEND_METHODS: readonly string[] = ['MANUAL', 'AUTO']

/** WAITING 이고 직접 발송(MANUAL·RESEND·CHANGE) 후 5분 이내인지. 서버·브라우저 시계 오차 1분까지는 방금으로 본다. */
function isJustSent(task: PartnerStatusLineTask, nowMs: number): boolean {
  const cr = task.confirmRequest
  if (task.partnerStatus !== PARTNER_STATUS.WAITING || !cr || !DIRECT_SEND_METHODS.includes(String(cr.sendMethod))) return false
  const sentMs = parseLocalDtmMs(task.sentDtm ?? cr.sentDtm)
  if (sentMs == null) return false
  const diff = nowMs - sentMs
  return diff >= -60_000 && diff <= JUST_SENT_WINDOW_MS
}

/** 차순위 회신 대기 여부 — WAITING·마지막 전달 건 MANUAL/AUTO·rank≥2(RESEND·CHANGE 제외). */
function isNextRank(task: PartnerStatusLineTask): boolean {
  const cr = task.confirmRequest
  return task.partnerStatus === PARTNER_STATUS.WAITING && !!cr && FIRST_SEND_METHODS.includes(String(cr.sendMethod)) && cr.rank >= 2
}

/** 미확정 문구 — rank≥2 이면 "{rank}순위까지 답이 없어요 · 할일확인". */
function unfixedText(cr: ConfirmRequest | null | undefined): string {
  return cr && cr.rank >= 2 ? `${cr.rank}순위까지 답이 없어요 · 할일확인` : '답이 없어요 · 할일확인'
}

/**
 * 상세창 시공파트너 카드 상태 줄 — 상태명(label)과 문구(text).
 * 기한은 confirmRequest.replyExpireDtm 이 있으면 항상 표시, 없으면 "· 기한 …" 부분만 생략한다.
 * 발송시각은 공종 sentDtm(요청 시각)을 쓴다.
 */
export function getPartnerStatusLine(task: PartnerStatusLineTask, nowMs: number): { label: string; text: string } {
  const view = getPartnerStatusView(task.partnerStatus)
  const cr = task.confirmRequest ?? null
  const resp = formatMonthDay(task.respondedDtm)
  switch (task.partnerStatus) {
    case PARTNER_STATUS.ASSIGNED:
      return {
        label: task.assignSource === ASSIGN_SOURCE.AUTO ? '자동배정' : '직접배정',
        // 결과만 직접등록(SIF-3)은 일정확인 카톡을 보내지 않는 반장 — 보내라는 듯한 문구를 쓰지 않는다
        text: task.commYn === 'N' ? '' : '아직 일정확인 카톡을 보내지 않았어요',
      }
    case PARTNER_STATUS.WAITING: {
      const exp = formatMonthDayTime(cr?.replyExpireDtm)
      const expPart = exp ? ` · 기한 ${exp}` : ''
      const sent = formatMonthDayTime(task.sentDtm ?? cr?.sentDtm)
      const just = isJustSent(task, nowMs)
      if (cr && cr.sendMethod === 'CHANGE') {
        const when = just ? '방금 발송' : sent ? `${sent} 발송` : '발송'
        return { label: '재회신 대기', text: `일정 변경 카톡 ${when}${expPart}` }
      }
      if (isNextRank(task) && cr) {
        if (just) return { label: '차순위 회신 대기', text: `방금 발송${expPart}` }
        const head = cr.previousOutcome === 'DECLINED' ? `${cr.rank - 1}순위 '불가' → ${cr.rank}순위 회신 대기` : `${cr.rank}순위 회신 대기`
        return { label: '차순위 회신 대기', text: `${head}${expPart}` }
      }
      if (just) return { label: view.label, text: `방금 발송${expPart}` }
      return { label: view.label, text: [sent && `${sent} 발송`, exp && `기한 ${exp}`].filter(Boolean).join(' · ') }
    }
    case PARTNER_STATUS.CONFIRMED:
      // 결과만 직접등록(SIF-3)은 회신 없이 배정으로 확정 — 없는 회신을 표시하지 않는다
      if (task.commYn === 'N') return { label: '일정 확정', text: '' }
      return { label: view.label, text: resp ? `${resp} '가능' 회신` : "'가능' 회신" }
    case PARTNER_STATUS.UNASSIGNABLE:
      return { label: view.label, text: '맡길 반장님이 없어요' }
    case PARTNER_STATUS.DECLINED:
      return { label: view.label, text: `${resp ? `${resp} ` : ''}'불가' 회신 · 다음 반장님 발송 전` }
    case PARTNER_STATUS.UNFIXED:
      return { label: view.label, text: unfixedText(cr) }
    default:
      return { label: view.label, text: '배정 전이에요' }
  }
}

/** 진행 요약 한 단계. state: done=완료 · current=지금 단계 · pending=아직 */
export interface ConfirmProgressStep {
  title: string
  text: string
  state: 'done' | 'current' | 'pending'
  /** 세부 내역 팝업이 있는 단계의 구분 키 — 있으면 단계 줄이 눌러 열 수 있는 링크가 된다(B-09·B-11) */
  detailKey?: string
  /** 단계 줄 끝 이동 링크(예: 결과만 직접등록 '작업현황 등록 ›') — 구분 키와 링크 이름 */
  action?: { key: string; label: string }
}

/**
 * 일정확인 진행 3단계(일정확인 요청 → 반장님 회신 → 일정 확정). 작업요청·작업보고 단계는 호출부가 뒤에 이어 붙일 수 있다.
 * @param task 공종 상태
 * @param ctx hasPartner=반장이 지정돼 있는지 · startDate·endDate=공종 착공·종료일
 */
export function getConfirmProgressSteps(
  task: PartnerStatusLineTask,
  ctx: { hasPartner: boolean; startDate: Date; endDate: Date },
): ConfirmProgressStep[] {
  const cr = task.confirmRequest ?? null
  const status = task.partnerStatus ?? PARTNER_STATUS.UNASSIGNED
  const sent = formatMonthDayTime(task.sentDtm ?? cr?.sentDtm)
  const resp = formatMonthDay(task.respondedDtm)
  const md = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`
  const confirmed = status === PARTNER_STATUS.CONFIRMED

  // 결과만 직접등록(SIF-3) — 일정확인 요청·회신을 주고받지 않으므로 1·2단계는 '-'(이전 반장에게 보낸 기록도 쓰지 않는다)
  const direct = task.commYn === 'N'

  // 1단계
  const step1Done = !direct && !!(task.sentDtm ?? cr?.sentDtm)
  const step1Text = direct ? '-'
    : step1Done
      ? `${sent} 카톡 발송${cr?.sendMethod === 'CHANGE' ? ' · 일정 변경 카톡 다시 발송' : ''}`
      : ctx.hasPartner ? '아직 보내지 않았어요' : '반장님을 먼저 골라 주세요'

  // 2단계 — 결과만 직접등록(SIF-3)은 반장 회신이 없으므로 '-'(확정은 3단계에만 보인다)
  let step2Text = '-'
  let step2Started = false
  if (!direct) switch (status) {
    case PARTNER_STATUS.CONFIRMED:
      step2Text = resp ? `${resp} '가능' 회신` : "'가능' 회신"; step2Started = true; break
    case PARTNER_STATUS.DECLINED:
      step2Text = `${resp ? `${resp} ` : ''}'불가' 회신 · 다음 반장님께 보내 주세요`; step2Started = true; break
    case PARTNER_STATUS.UNFIXED:
      step2Text = unfixedText(cr); step2Started = true; break
    case PARTNER_STATUS.WAITING: {
      const exp = formatMonthDayTime(cr?.replyExpireDtm)
      const prefix = isNextRank(task) && cr
        ? (cr.previousOutcome === 'DECLINED' ? `${cr.rank - 1}순위 '불가' → ` : `${cr.rank}순위 `)
        : ''
      step2Text = `${prefix}회신을 기다리는 중${exp ? ` · 기한 ${exp}` : ''}`; step2Started = true; break
    }
  }

  // 3단계
  const step3Text = confirmed ? `${md(ctx.startDate)} ~ ${md(ctx.endDate)} 작업 확정` : '-'

  // 결과만 직접등록은 요청·회신 단계가 없으므로 지금 단계는 늘 3단계(일정 확정)
  const current = confirmed || direct ? 3 : step2Started ? 2 : 1
  const st = (n: number, done: boolean): ConfirmProgressStep['state'] => n === current ? 'current' : done ? 'done' : 'pending'
  return [
    { title: '일정확인 요청', text: step1Text, state: st(1, step1Done) },
    { title: '반장님 회신',   text: step2Text, state: st(2, step2Started && status !== PARTNER_STATUS.WAITING) },
    { title: '일정 확정',     text: step3Text, state: st(3, confirmed) },
  ]
}
